/**
 * Sign in, sign up, and reset.
 *
 * One screen with three modes rather than three routes: an unauthenticated visitor
 * who mistyped their password should not lose what they typed to a navigation, and
 * "I meant to sign up" is one click rather than a page load.
 */

import { useState, type FormEvent } from 'react'
import { Button, Card, Field, TextInput } from '@modulus/ui'
import { api } from '../../lib/api'
import { useSession } from '../../lib/session'

type Mode = 'sign-in' | 'sign-up' | 'reset'

export function Auth() {
  const { signIn, signUp } = useSession()
  const [mode, setMode] = useState<Mode>('sign-in')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)

    try {
      if (mode === 'sign-in') {
        await signIn(email, password)
      } else if (mode === 'sign-up') {
        const result = await signUp(email, password, displayName.trim())
        if (result === 'verify') {
          setNotice('Check your inbox — confirm your email, then sign in.')
          setMode('sign-in')
        }
      } else {
        await api.auth.requestPasswordReset(email)
        // Always the same message. Telling somebody whether an address is
        // registered turns this box into an account-enumeration oracle.
        setNotice('If that address has an account, a reset link is on its way.')
        setMode('sign-in')
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const titles: Record<Mode, { title: string; subtitle: string; cta: string }> = {
    'sign-in': {
      title: 'Welcome back',
      subtitle: 'Pick up where you left off.',
      cta: 'Sign in',
    },
    'sign-up': {
      title: 'Start with Modulus',
      subtitle: 'Practice that marks you the way the real thing does.',
      cta: 'Create account',
    },
    reset: {
      title: 'Reset your password',
      subtitle: 'We’ll email you a link.',
      cta: 'Send reset link',
    },
  }
  const copy = titles[mode]

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-m)',
      }}
    >
      <Card style={{ width: 'min(420px, 100%)' }}>
        <div className="mx-row" style={{ marginBottom: 'var(--space-l)' }}>
          {/* Self-closing, like the one in the shell. The glyph is a mask on
              `::before`; a text child overflows the 32px box and renders as a
              loose lowercase m under the mark. */}
          <span className="mx-brand-mark" aria-hidden="true" />
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--text-lg)' }}>
            Modulus
          </span>
        </div>

        <h1 className="mx-page-title" style={{ fontSize: 'var(--text-xl)' }}>
          {copy.title}
        </h1>
        <p className="mx-page-subtitle" style={{ marginBottom: 'var(--space-l)' }}>
          {copy.subtitle}
        </p>

        <form onSubmit={(event) => void submit(event)} className="mx-stack" style={{ gap: 'var(--space-m)' }}>
          {mode === 'sign-up' && (
            <Field label="Your name">
              {(props) => (
                <TextInput
                  {...props}
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Alex"
                />
              )}
            </Field>
          )}

          <Field label="Email">
            {(props) => (
              <TextInput
                {...props}
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu"
              />
            )}
          </Field>

          {mode !== 'reset' && (
            <Field
              label="Password"
              {...(mode === 'sign-up' ? { hint: 'At least 8 characters.' } : {})}
            >
              {(props) => (
                <TextInput
                  {...props}
                  type="password"
                  autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
                  required
                  minLength={mode === 'sign-up' ? 8 : undefined}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              )}
            </Field>
          )}

          {error && (
            <p className="mx-field-error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--info)' }} role="status">
              {notice}
            </p>
          )}

          <Button type="submit" size="lg" block busy={busy}>
            {copy.cta}
          </Button>
        </form>

        <div className="mx-row" style={{ marginTop: 'var(--space-l)', gap: 'var(--space-m)' }}>
          {mode !== 'sign-in' && (
            <button type="button" className="mx-btn mx-btn-ghost mx-btn-sm" onClick={() => setMode('sign-in')}>
              Sign in instead
            </button>
          )}
          {mode !== 'sign-up' && (
            <button type="button" className="mx-btn mx-btn-ghost mx-btn-sm" onClick={() => setMode('sign-up')}>
              Create an account
            </button>
          )}
          {mode === 'sign-in' && (
            <button type="button" className="mx-btn mx-btn-ghost mx-btn-sm" onClick={() => setMode('reset')}>
              Forgot password
            </button>
          )}
        </div>
      </Card>
    </div>
  )
}
